import React from "react";
import { DollarSign } from "react-feather";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetCollectionsSummaryQuery } from "../../collections/collectionsApi";
import dayjs from "dayjs";
import { useResolvedYesterday } from "../../masters/holidays/useWorkingDay";
import { dateRanges, money, percentChange, sumBy } from "../dashboardHelpers";
import KpiCard from "../components/KpiCard";
import { paths } from "../../../routes/paths";

// Every dashboard widget is a fully self-contained component: it decides
// its own visibility (permission + "is there anything to show"), fetches
// its own data, and renders its own grid column (size class included on
// its own root element) - see widgets/registry.js for why. Adding a new
// widget later never means touching Dashboard.jsx or any other widget.
const TodaysCollectionKpi = () => {
  const canView = useHasPermission("Collections.View");
  const { selectedBranchId } = useBranchFilter();
  const dr = dateRanges();
  // Compared with the last WORKING day (2026-09-14) - on a Monday "vs Yesterday"
  // compared with Sunday's 0 and always read as a huge rise.
  const yesterday = useResolvedYesterday({ skip: !canView });

  const { data: todaySummary, isFetching } = useGetCollectionsSummaryQuery(
    { branchId: selectedBranchId ?? undefined, dateFrom: dr.today, dateTo: dr.today },
    { skip: !canView }
  );
  const { data: yestSummary } = useGetCollectionsSummaryQuery(
    { branchId: selectedBranchId ?? undefined, dateFrom: yesterday.date, dateTo: yesterday.date },
    { skip: !canView || yesterday.isResolving }
  );

  if (!canView) return null;

  const todayTotal = sumBy(todaySummary, "totalAmount");
  const yesterdayTotal = sumBy(yestSummary, "totalAmount");

  return (
    <div className="col-6 col-xl-3">
      <KpiCard
        icon={<DollarSign size={20} />}
        iconBg="#C0392B"
        label="Today's Collection"
        value={isFetching ? "..." : money(todayTotal)}
        trendLabel={yesterday.shifted ? `vs ${dayjs(yesterday.date).format("ddd")} (last working day)` : "vs Yesterday"}
        trendPercent={percentChange(todayTotal, yesterdayTotal)}
        currentValue={todayTotal}
        comparisonValue={yesterdayTotal}
        compareRangeLabel={yesterday.date}
        // The figure is today's collections for the selected branch, so the
        // link carries exactly that - the date has to ride in the URL because
        // list pages don't read the dashboard's period.
        drillTo={paths.collectionsFiltered({ branchId: selectedBranchId ?? undefined, dateFrom: dr.today, dateTo: dr.today })}
        drillTitle="See today's collection records"
      />
    </div>
  );
};

export default TodaysCollectionKpi;
